/**
 * Dashboard entry point to the letter-first home.
 *
 * Recordings and export live on the letter-first Saved screen; without
 * this card a user who reached the Dashboard had no path back to their
 * letters. Shown whenever the user has a letter (draft or saved):
 * saved → the Saved screen (view, export, recordings), draft → the
 * canvas to finish it.
 */
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';

import { letterToLovedOnesService } from '../services/letterToLovedOnesInfo';
import { createLogger } from '../utils/logger';
import { ms, s } from '../utils/responsive';

import { AppText as Text } from './AppText';

const logger = createLogger('LettersQuickAccessCard');

type Target =
  | { screen: 'LetterSaved' | 'LetterCanvas'; letterId: string }
  | null;

export const LettersQuickAccessCard: React.FC = () => {
  const navigation = useNavigation<any>();
  const [target, setTarget] = useState<Target>(null);
  const [salutation, setSalutation] = useState('');

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      letterToLovedOnesService
        .getAllLettersToLovedOnes()
        .then((letters) => {
          if (cancelled) return;
          const saved = letters.find((l) => (l.status ?? 'saved') === 'saved');
          const draft = letters.find((l) => l.status === 'draft');
          if (saved) {
            setTarget({ screen: 'LetterSaved', letterId: saved.id });
            setSalutation(saved.salutation || saved.recipientName || '');
          } else if (draft) {
            setTarget({ screen: 'LetterCanvas', letterId: draft.id });
            setSalutation(draft.salutation || draft.recipientName || '');
          } else {
            setTarget(null);
          }
        })
        .catch((error) => {
          logger.debug('Letter check failed (card hidden):', error);
        });
      return () => {
        cancelled = true;
      };
    }, []),
  );

  if (!target) return null;

  const isDraft = target.screen === 'LetterCanvas';

  return (
    <TouchableOpacity
      style={styles.card}
      onPress={() =>
        // A lone draft goes straight back to the canvas; otherwise the
        // letters home lists everything — letters, recordings, delivery.
        navigation.navigate('LetterFirst', {
          screen: isDraft ? 'LetterCanvas' : 'LettersHome',
          params: isDraft ? { letterId: target.letterId } : undefined,
        })
      }
      activeOpacity={0.8}
      accessibilityRole="button"
      accessibilityLabel={isDraft ? 'Finish your letter' : 'Your letters'}
      accessibilityHint={
        isDraft
          ? 'Double tap to continue writing your letter'
          : 'Double tap to view, export, or record a reading of your letter'
      }
    >
      <View style={styles.textWrap}>
        <Text style={styles.title}>
          {isDraft ? 'Finish your letter' : 'Your letters'}
        </Text>
        <Text style={styles.subtitle}>
          {isDraft
            ? `Your letter${salutation ? ` to ${salutation}` : ''} is waiting — saved as you left it.`
            : `View, export, or record a reading${salutation ? ` — to ${salutation}` : ''}.`}
        </Text>
      </View>
      <Text style={styles.arrow} accessibilityElementsHidden>
        ›
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#C7E9EB',
    padding: ms(14),
    marginHorizontal: ms(16),
    marginBottom: ms(12),
  },
  textWrap: {
    flex: 1,
  },
  title: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  subtitle: {
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#6B7280',
    marginTop: ms(2),
  },
  arrow: {
    fontSize: ms(24, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
    marginLeft: ms(8),
  },
  spacer: {
    width: s(4),
  },
});

export default LettersQuickAccessCard;
